import { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FolderOpen as FolderIcon, ContentCopy as CopyIcon, KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import { handlebarsToJsonSchemaV2 } from '../handlebarsToJsonSchema';
import { FolderManagerMoveDialog } from '../../../../../../../../FolderManager/Components/FolderManagerMoveDialog';
import { useGetUserRootFoldersQuery, useGetFolderPathQuery } from '../../../../../../../../../services';

// Styled Components
const Container = styled.div`
  padding: 24px;
  background: #ffffff;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Header = styled.div`
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
`;

const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
`;

const FieldCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #007bff;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
  }
`;

const FieldHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const FieldNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldName = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #1a1a1a;
  background: #ffffff;
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid #dee2e6;
`;

const OrderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #007bff;
    background: #f0f7ff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    font-size: 18px;
    color: #495057;
  }
`;

const FieldContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  min-width: 120px;
`;

const StyledSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  min-width: 150px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const StyledInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  min-width: 200px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
`;

const SaveButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background: #0056b3;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #6c757d;
  font-size: 16px;
`;

const FolderPickerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  min-width: 200px;
  transition: all 0.2s ease;
  text-align: left;
  justify-content: flex-start;

  &:hover {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    background: #f8f9fa;
    color: #6c757d;
    cursor: not-allowed;
    border-color: #e9ecef;
  }

  .folder-icon {
    color: #6c757d;
    font-size: 18px;
  }

  .folder-path {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .placeholder {
    color: #6c757d;
    font-style: italic;
  }
`;

const FilterInfo = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 4px;
  font-style: italic;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background: #007bff;
  color: white;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 8px;

  &:hover {
    background: #0056b3;
    border-color: #0056b3;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #6c757d;
    border-color: #6c757d;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  .copy-icon {
    font-size: 14px;
  }
`;

const QueryKeyContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const RowContainer = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
`;

const RangeInputsContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const RangeInput = styled(StyledInput)`
  min-width: 80px;
  max-width: 100px;
`;

const RangeLabel = styled.span`
  font-size: 12px;
  color: #6c757d;
  white-space: nowrap;
`;

interface Props {
  entrypoint: { name: string; html: string; displayName?: string; instanceId?: string };
  blocks: { name: string; html: string | null }[];
  onSave: (schema: any, displayName?: string) => void;
  schema: any;
}

const ImagePickerInputSettings = ({
  field,
  onUpdateInputSchema,
}: {
  field: any;
  onUpdateInputSchema: (schema: ImagePickerDynamicFormField) => void;
}) => {
  const variantOptions: ImagePickerDynamicFormField['inputOptions']['pickerVariant'][] = ['default', 'carousel'];

  // State for folder picker dialog
  const [moveDialogState, setMoveDialogState] = useState({
    opened: false,
    rootFolder: null,
    selectedItems: [],
  });

  // Get user root folders to use as the root for folder selection
  const { data: userRootFolders } = useGetUserRootFoldersQuery();
  const rootFolder = userRootFolders?.find((f) => f.slug === 'company-assets'); // Use the first root folder
  // console.log(userRootFolders);
  // Get folder path for display
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const { data: folderBreadcrumbs } = useGetFolderPathQuery(
    { folderId: selectedFolderId! },
    { skip: !selectedFolderId },
  );

  const folderPath = useMemo(() => {
    if (!folderBreadcrumbs || folderBreadcrumbs.length === 0) {
      return field.inputOptions.sourcePath || '';
    }
    return folderBreadcrumbs.map((item) => item?.name).join('/');
  }, [folderBreadcrumbs, field.inputOptions.sourcePath]);

  const setSourceFolder = (source: string) => {
    onUpdateInputSchema({
      ...field,
      inputOptions: {
        ...field.inputOptions,
        sourcePath: source,
      },
    });
  };

  const setVariant = (variant: ImagePickerDynamicFormField['inputOptions']['pickerVariant']) => {
    onUpdateInputSchema({
      ...field,
      inputOptions: {
        ...field.inputOptions,
        pickerVariant: variant,
      },
    });
  };

  const handleFolderSelectClick = () => {
    if (!rootFolder) {
      console.warn('Could not open folder selector: no root folder available!');
      return;
    }

    setMoveDialogState({
      opened: true,
      rootFolder: rootFolder as any,
      selectedItems: [],
    });
  };

  const handlePickDestinationFolder = (data: any) => {
    if (!data) {
      setMoveDialogState({ opened: false, rootFolder: null, selectedItems: [] });
      return;
    }

    const targetFolderId = data.destinationId as number;
    setSelectedFolderId(targetFolderId);

    // Update the source path with the selected folder path
    // We'll use the folder ID for now, but you might want to use the actual path
    setSourceFolder(`crn:folders:${targetFolderId}`);

    setMoveDialogState({ opened: false, rootFolder: null, selectedItems: [] });
  };

  return (
    <>
      <OptionsContainer>
        <RowContainer>
          <Label>Source Folder:</Label>
          <FolderPickerButton type="button" onClick={handleFolderSelectClick} disabled={!rootFolder}>
            <FolderIcon className="folder-icon" />
            <span className={folderPath ? 'folder-path' : 'placeholder'}>{folderPath || 'Select a folder...'}</span>
          </FolderPickerButton>
        </RowContainer>
        <RowContainer>
          <Label>Variant:</Label>
          <StyledSelect
            value={field.inputOptions.pickerVariant}
            onChange={(e) => setVariant(e.target.value as ImagePickerDynamicFormField['inputOptions']['pickerVariant'])}
          >
            {variantOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </StyledSelect>
        </RowContainer>
      </OptionsContainer>

      {moveDialogState?.opened && (
        <FolderManagerMoveDialog
          dialogState={moveDialogState as any}
          submitLabel="Select Folder"
          onDismiss={handlePickDestinationFolder}
          includeUserRoot={false}
          targetOnly
          zIndex={99}
        />
      )}
    </>
  );
};

/**
 * Filter blocks that match the expected schema structure
 */
const filterBlocksBySchema = (blocks: any[], expectedSchema: any, keyName: string): any[] => {
  if (!expectedSchema?.properties) {
    console.log('No expected schema properties, returning all blocks');
    return blocks;
  }

  const expectedFields = Object.keys(expectedSchema.properties);

  const filteredBlocks = blocks.filter((block) => {
    const blockName = block.name;

    if (!block.html) {
      console.log(`Block ${blockName} has no HTML, excluding`);
      return false;
    }

    try {
      // parse the block's HTML to get its schema
      const blockSchema = handlebarsToJsonSchemaV2(blockName, [{ name: blockName, partial: block.html }]);

      // check if the block schema has properties
      if (!blockSchema?.properties) {
        console.log(`Block ${blockName} has no schema properties, excluding`);
        return false;
      }

      const blockFields = Object.keys(blockSchema.properties);

      // check if at least some expected fields are present in the block schema
      const matchingFields = expectedFields.filter((field) =>
        blockFields.some(
          (bField) => bField === field && blockSchema.properties[bField].type === expectedSchema.properties[field].type,
        ),
      );

      // allow blocks that have at least one matching field
      const hasSomeMatchingFields = matchingFields.length > 0;

      if (!hasSomeMatchingFields) {
        // console.log(`Block ${blockName} has no matching fields, excluding`);
      }

      return hasSomeMatchingFields;
    } catch (error) {
      console.warn(`Error parsing schema for block ${blockName}:`, error);
      return false;
    }
  });

  return filteredBlocks;
};

const QueryKeyInputSettings = ({
  field,
  onUpdateInputSchema,
}: {
  field: any;
  onUpdateInputSchema: (schema: QueryKeyDynamicFormField) => void;
}) => {
  const setGqlQueryKey = (key: string) => {
    onUpdateInputSchema({
      ...field.value,
      inputOptions: {
        ...field.value.inputOptions,
        gqlQueryKey: key,
      },
    });
  };

  return (
    <>
      <RowContainer>
        <Label>GQL Query Key:</Label>
        <StyledInput
          type="text"
          placeholder="e.g. resources"
          value={field.value.inputOptions?.gqlQueryKey || ''}
          onChange={(e) => setGqlQueryKey(e.target.value)}
        />
      </RowContainer>
    </>
  );
};

const OrderedListInputSettings = ({
  field,
  blocks,
  onUpdateInputSchema,
}: {
  field: any;
  blocks: any[];
  onUpdateInputSchema: (schema: OrderedListDynamicFormField) => void;
}) => {
  // State for copy feedback
  const [copySuccess, setCopySuccess] = useState(false);

  // Filter blocks that match the expected schema for array items
  const filteredBlocks = useMemo(() => {
    const itemsSchema = field.value.items;
    if (!itemsSchema) {
      return blocks;
    }

    return filterBlocksBySchema(blocks, itemsSchema, field.name);
  }, [blocks, field]);

  const setPreviewComponent = (name: string) => {
    onUpdateInputSchema({
      ...field.value,
      inputOptions: {
        ...field.value.inputOptions,
        previewComponent: name,
      },
    });
  };

  const setGqlQueryKey = (key: string) => {
    onUpdateInputSchema({
      ...field.value,
      inputOptions: {
        ...field.value.inputOptions,
        gqlQueryKey: key,
      },
    });
  };

  const setMinItems = (value: string) => {
    const numValue = parseInt(value, 10);

    if (isNaN(numValue) || value === '' || numValue === 0) {
      onUpdateInputSchema({
        ...field.value,
        minItems: undefined,
      });
    } else {
      onUpdateInputSchema({
        ...field.value,
        minItems: numValue,
      });
    }
  };

  const setMaxItems = (value: string) => {
    const numValue = parseInt(value, 10);

    if (isNaN(numValue) || value === '') {
      onUpdateInputSchema({
        ...field.value,
        maxItems: undefined,
      });
    } else {
      onUpdateInputSchema({
        ...field.value,
        maxItems: numValue,
      });
    }
  };

  const setFilterType = (filterType: string) => {
    onUpdateInputSchema({
      ...field.value,
      inputOptions: {
        ...field.value.inputOptions,
        filterType: filterType || undefined,
      },
    });
  };

  // Generate GraphQL query template
  const generateGraphQLTemplate = () => {
    const alias = field.value.inputOptions.gqlQueryKey || 'myAlias';
    const itemsSchema = field.value.items;

    if (!itemsSchema?.properties) {
      return `${alias}: REPLACE_ME {\n  ID\n}`;
    }

    const keys = Object.keys(itemsSchema.properties);
    const formattedKeys = keys.map((key) => `    ${key} : REPLACE_ME`).join('\n');

    return `${alias}: REPLACE_ME {\n    ID\n${formattedKeys}\n  }`;
  };

  const handleCopyTemplate = async () => {
    const template = generateGraphQLTemplate();

    try {
      await navigator.clipboard.writeText(template);
      setCopySuccess(true);

      // Reset the success state after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy template to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = template;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    }
  };

  return (
    <OptionsContainer>
      <RowContainer>
        <Label>GQL Query Key:</Label>
        <QueryKeyContainer>
          <StyledInput
            type="text"
            placeholder="override gql query key"
            value={field.value.inputOptions.gqlQueryKey ?? ''}
            onChange={(e) => setGqlQueryKey(e.target.value)}
          />
          <CopyButton
            type="button"
            onClick={handleCopyTemplate}
            disabled={!field.value.inputOptions.gqlQueryKey}
            title="Copy GraphQL query template"
            style={{
              background: copySuccess ? '#28a745' : undefined,
              borderColor: copySuccess ? '#28a745' : undefined,
            }}
          >
            <CopyIcon className="copy-icon" />
            {copySuccess ? 'Copied!' : 'Copy Query'}
          </CopyButton>
        </QueryKeyContainer>
      </RowContainer>

      <RowContainer>
        <Label>Preview Component:</Label>
        <div>
          <StyledSelect
            value={field.value.inputOptions.previewComponent ?? ''}
            onChange={(e) => setPreviewComponent(e.target.value)}
          >
            <option value="">None</option>
            {filteredBlocks.map((block) => (
              <option key={block.name} value={block.name}>
                {block.name}
              </option>
            ))}
          </StyledSelect>
          {filteredBlocks.length !== blocks.length && (
            <FilterInfo>
              Showing {filteredBlocks.length} of {blocks.length} blocks (filtered by schema compatibility)
            </FilterInfo>
          )}
        </div>
      </RowContainer>

      <RowContainer>
        <Label>Item Range:</Label>
        <RangeInputsContainer>
          <div>
            <RangeLabel>Min:</RangeLabel>
            <RangeInput
              type="number"
              placeholder="0"
              min="0"
              value={field.value.minItems ?? ''}
              onChange={(e) => setMinItems(e.target.value)}
            />
          </div>
          <div>
            <RangeLabel>Max:</RangeLabel>
            <RangeInput
              type="number"
              placeholder="∞"
              min="1"
              value={field.value.maxItems ?? ''}
              onChange={(e) => setMaxItems(e.target.value)}
            />
          </div>
        </RangeInputsContainer>
      </RowContainer>

      <RowContainer>
        <Label>Filter Type:</Label>
        <div>
          <StyledSelect
            value={field.value.inputOptions.filterType ?? ''}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">None</option>
            <option value="text">Text</option>
            <option value="attributes">Attributes</option>
          </StyledSelect>
        </div>
      </RowContainer>
    </OptionsContainer>
  );
};

const InputSettings = ({
  field,
  blocks,
  onUpdateInputSchema,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: {
  field: any;
  blocks: any[];
  onUpdateInputSchema: (schema: DynamicFormField) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}) => {
  const inputTypeOptions: DynamicFormField['inputType'][] = useMemo(() => {
    switch (field.value.inputType) {
      case 'ordered-list':
      case 'queryKey':
        return ['ordered-list', 'queryKey'];
      default:
        return ['email', 'hidden', 'image-picker', 'number', 'select', 'text', 'textarea', 'video-picker', 'rich-text', 'checkbox'];
    }
  }, [field.value.inputType]);

  const setInputType = (inputType: DynamicFormField['inputType']) => {
    switch (inputType) {
      case 'image-picker':
        onUpdateInputSchema({
          ...field.value,
          inputType: 'image-picker',
          type: 'number',
          inputOptions: { pickerVariant: 'default', sourcePath: '' },
        });
        break;

      case 'ordered-list':
        onUpdateInputSchema({
          ...field.value,
          inputType: 'ordered-list',
          type: 'array',
          inputOptions: { variant: 'default' },
        });
        break;

      case 'queryKey':
        onUpdateInputSchema({
          ...field.value,
          inputType: 'queryKey',
          type: 'array',
          inputOptions: { gqlQueryKey: field.value.inputOptions?.gqlQueryKey },
        });
        break;

      case 'checkbox':
        onUpdateInputSchema({
          ...field.value,
          inputType: 'checkbox',
          type: 'boolean',
          inputOptions: {},
        });
        break;

      default:
        onUpdateInputSchema({
          ...field.value,
          inputType: inputType,
          type: 'string',
          inputOptions: {},
        });
        break;
    }
  };

  const setDisplayName = (displayName: string) => {
    onUpdateInputSchema({
      ...field.value,
      displayName: displayName?.trim(),
    });
  };

  return (
    <FieldCard>
      <FieldHeader>
        <FieldName>{field.name}</FieldName>
        <FieldNameContainer>
          <StyledSelect
            value={field.value.inputType}
            onChange={(e) => setInputType(e.target.value as DynamicFormField['inputType'])}
          >
            {inputTypeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </StyledSelect>
          <OrderButton type="button" onClick={onMoveUp} disabled={isFirst} title="Move up">
            <KeyboardArrowUp />
          </OrderButton>
          <OrderButton type="button" onClick={onMoveDown} disabled={isLast} title="Move down">
            <KeyboardArrowDown />
          </OrderButton>
        </FieldNameContainer>
      </FieldHeader>

      <FieldContent>
        <RowContainer>
          <Label>Display Name:</Label>
          <StyledInput
            type="text"
            placeholder={`Display name for "${field.name}"`}
            value={field.value.displayName || ''}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </RowContainer>

        {field.value.inputType === 'image-picker' && (
          <ImagePickerInputSettings field={field.value} onUpdateInputSchema={onUpdateInputSchema} />
        )}

        {field.value.inputType === 'ordered-list' && (
          <OrderedListInputSettings field={field} onUpdateInputSchema={onUpdateInputSchema} blocks={blocks} />
        )}

        {field.value.inputType === 'queryKey' && (
          <QueryKeyInputSettings field={field} onUpdateInputSchema={onUpdateInputSchema} />
        )}
      </FieldContent>
    </FieldCard>
  );
};

export const BlockInputsSettings: FC<Props> = ({ entrypoint, blocks, onSave, ...props }) => {
  const [schema, setSchema] = useState<any>(props.schema);

  // Get displayName from schema like ValuesPage does
  const [displayName, setDisplayName] = useState<string>(
    props.schema?.displayName || props.schema?.title || entrypoint.displayName || entrypoint.name,
  );

  const updateInputSchema = (fieldName: string, schema: any) => {
    setSchema((prev: any) => ({
      ...prev,
      properties: {
        ...prev.properties,
        [fieldName]: schema,
      },
    }));
  };

  const moveField = (fieldName: string, direction: 'up' | 'down') => {
    setSchema((prev: any) => {
      const entries = Object.entries(prev.properties);

      // Sort by current order
      const sortedEntries = entries.sort(([, a]: any, [, b]: any) => {
        const orderA = a.inputOptions?.order ?? 0;
        const orderB = b.inputOptions?.order ?? 0;
        return orderA - orderB;
      });

      // Find the index of the field to move
      const currentIndex = sortedEntries.findIndex(([name]) => name === fieldName);
      if (currentIndex === -1) return prev;

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      // Don't move if at boundaries
      if (targetIndex < 0 || targetIndex >= sortedEntries.length) return prev;

      // Swap the items
      [sortedEntries[currentIndex], sortedEntries[targetIndex]] = [sortedEntries[targetIndex], sortedEntries[currentIndex]];

      // Reassign order values based on new positions
      const updatedProperties: any = {};
      sortedEntries.forEach(([name, value]: any, index) => {
        updatedProperties[name] = {
          ...value,
          inputOptions: {
            ...value.inputOptions,
            order: index,
          },
        };
      });

      return {
        ...prev,
        properties: updatedProperties,
      };
    });
  };

  useEffect(() => {
    if (props.schema) {
      setSchema(props.schema);
    } else {
      const schema = handlebarsToJsonSchemaV2(
        `${entrypoint.name}__wrapper`,
        blocks
          .map((b) => ({ name: b.name, partial: b.html || '' }))
          .concat([
            { name: entrypoint.name, partial: entrypoint.html },
            { name: `${entrypoint.name}__wrapper`, partial: `{{> ${entrypoint.name} }}` },
          ]),
      );

      setSchema(schema?.properties[entrypoint.name]);
    }
  }, [props.schema]);

  const hasFields = schema?.properties && Object.keys(schema.properties).length > 0;

  // Get sorted field entries
  const sortedFieldEntries = useMemo(() => {
    if (!schema?.properties) return [];

    return Object.entries(schema.properties).sort(([, a]: any, [, b]: any) => {
      const orderA = a.inputOptions?.order ?? 0;
      const orderB = b.inputOptions?.order ?? 0;
      return orderA - orderB;
    });
  }, [schema]);

  return (
    <Container>
      <Header>
        <Title>Block Input Settings</Title>
        <Subtitle>Configure input types and options for "{entrypoint.name}" block</Subtitle>
      </Header>

      {/* Display Name Configuration */}
      <FieldCard style={{ marginBottom: '24px' }}>
        <FieldHeader>
          <FieldName>Block Configuration</FieldName>
        </FieldHeader>
        <FieldContent>
          <RowContainer>
            <Label>Display Name:</Label>
            <StyledInput
              type="text"
              placeholder="Enter display name for this block instance"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </RowContainer>
        </FieldContent>
      </FieldCard>

      {hasFields ? (
        <FieldsContainer>
          {sortedFieldEntries.map(([k, v]: any[], index) => {
            return (
              <InputSettings
                key={k}
                field={{ name: k, value: v }}
                blocks={blocks}
                onUpdateInputSchema={(schema) => updateInputSchema(k, schema)}
                onMoveUp={() => moveField(k, 'up')}
                onMoveDown={() => moveField(k, 'down')}
                isFirst={index === 0}
                isLast={index === sortedFieldEntries.length - 1}
              />
            );
          })}
        </FieldsContainer>
      ) : (
        <EmptyState>
          No input fields found for this block.
          <br />
          Make sure the block template contains Handlebars expressions.
        </EmptyState>
      )}

      <SaveButton type="button" onClick={() => onSave(schema, displayName)}>
        Save Configuration
      </SaveButton>
    </Container>
  );
};
